import { Controller } from "../vendor/stimulus.js";

const USER_EVENTS = [
  "wheel",
  "touchstart",
  "pointerdown",
  "keydown"
];
const SCROLL_SPEED = 0.5;
const AUTOSCROLL_CLASS = "autoscroll";
const AUTOSCROLL_INITIALIZED_CLASS = "initialized";
const SELECTED_CLASS = "selected";

export default class extends Controller {
  connect() {
    this.listElement = this.element.querySelector(".suggestions");
    if (!this.listElement) return;

    this.inputElement = this.resolveInputElement();
    this.userInterrupted = false;
    this.direction = 1;
    this.isAnimating = false;
    this.offsetAccumulator = 0;
    this.maxScrollLeft = 0;
    this.handleScroll = this.onScroll.bind(this);
    this.handleUserEvent = this.stopAutoscroll.bind(this);
    this.handleSuggestionClick =
      this.handleSuggestionClick || ((event) => this.onSuggestionClick(event));

    this.listElement.addEventListener("scroll", this.handleScroll, { passive: true });
    USER_EVENTS.forEach((eventName) => {
      this.listElement.addEventListener(eventName, this.handleUserEvent, { passive: true });
    });
    this.listElement.addEventListener("click", this.handleSuggestionClick);

    this.createVisibilityObserver();
    this.observeListSize();
    this.startAutoscroll();
  }

  disconnect() {
    if (!this.listElement) return;

    this.listElement.removeEventListener("scroll", this.handleScroll);
    USER_EVENTS.forEach((eventName) => {
      this.listElement.removeEventListener(eventName, this.handleUserEvent);
    });
    if (this.handleSuggestionClick) {
      this.listElement.removeEventListener("click", this.handleSuggestionClick);
    }

    this.cancelAnimation();
    if (this.visibilityRestartId) {
      window.cancelAnimationFrame(this.visibilityRestartId);
      this.visibilityRestartId = null;
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
    if (this.sizeObserver) {
      this.sizeObserver.disconnect();
      this.sizeObserver = null;
    }
    this.listElement = null;
    this.inputElement = null;
    this.setAutoscrollClass(false);
  }
  onSuggestionClick(event) {
    if (!this.listElement) return;
    const button = event.target.closest("button[data-prompt]");
    if (!button || !this.listElement.contains(button)) return;
    const prompt = (button.dataset.prompt || button.textContent || "").trim();
    if (!prompt) return;

    const input = this.inputElement || this.resolveInputElement();
    if (!input) return;

    this.clearSelectedSuggestions();
    button.classList.add(SELECTED_CLASS);

    input.value = prompt;
    input.focus();
    this.dispatchEvent("input", {}, input);
  }

  clearSelectedSuggestions() {
    if (!this.listElement) return;
    this.listElement.querySelectorAll(`.${SELECTED_CLASS}`).forEach((node) => {
      node.classList.remove(SELECTED_CLASS);
    });
  }

  resolveInputElement() {
    const inputId = this.element?.dataset?.inputId;
    if (!inputId) return null;
    return document.getElementById(inputId) || null;
  }

  startAutoscroll() {
    if (!this.listElement || this.userInterrupted || this.rafId) return;

    this.maxScrollLeft = this.measureMaxScrollLeft();

    this.direction = 1;
    this.offsetAccumulator = 0;
    this.setAutoscrollClass(true);
    this.scheduleStep();
  }

  scheduleStep() {
    this.rafId = window.requestAnimationFrame(() => this.step());
  }

  step() {
    if (this.userInterrupted || !this.listElement) return;

    this.startAnimating();
    const { delta, remainder } = this.computeIntegerDelta();
    this.offsetAccumulator = remainder;
    if (delta === 0) {
      this.stopAnimating();
      this.scheduleStep();
      return;
    }

    const next = this.listElement.scrollLeft + delta;
    const clamped = Math.min(this.maxScrollLeft, Math.max(0, next));
    this.listElement.scrollLeft = clamped;
    this.stopAnimating();

    if (clamped >= this.maxScrollLeft && this.direction > 0) {
      this.direction = -1;
      this.offsetAccumulator = 0;
    } else if (clamped <= 0 && this.direction < 0) {
      this.direction = 1;
      this.offsetAccumulator = 0;
    }

    this.scheduleStep();
  }

  onScroll(event) {
    if (this.isAnimating || this.userInterrupted) return;
    if (event && event.isTrusted === false) return;
    this.stopAutoscroll();
  }

  stopAutoscroll() {
    if (this.userInterrupted) return;
    this.userInterrupted = true;
    this.cancelAnimation();
  }

  cancelAnimation() {
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.animatingResetId) {
      window.cancelAnimationFrame(this.animatingResetId);
      this.animatingResetId = null;
    }
    this.setAutoscrollClass(false);
  }

  startAnimating() {
    this.isAnimating = true;
    if (this.animatingResetId) {
      window.cancelAnimationFrame(this.animatingResetId);
      this.animatingResetId = null;
    }
  }

  stopAnimating() {
    if (this.animatingResetId) {
      window.cancelAnimationFrame(this.animatingResetId);
    }
    this.animatingResetId = window.requestAnimationFrame(() => {
      this.isAnimating = false;
      this.animatingResetId = null;
    });
  }

  createVisibilityObserver() {
    if (!("IntersectionObserver" in window)) return;

    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.restartAutoscrollAfterVisibility();
        }
      });
    });
    this.visibilityObserver.observe(this.listElement);
  }

  observeListSize() {
    if (!("ResizeObserver" in window)) return;

    this.sizeObserver = new ResizeObserver(() => this.handleListResize());
    this.sizeObserver.observe(this.listElement);
  }

  handleListResize() {
    if (!this.listElement) return;

    const nextMax = this.measureMaxScrollLeft();
    if (nextMax === this.maxScrollLeft) return;

    this.maxScrollLeft = nextMax;
    if (!this.userInterrupted && !this.rafId) {
      this.startAutoscroll();
    }
  }

  measureMaxScrollLeft() {
    if (!this.listElement) return 1;
    const maxValue = this.listElement.scrollWidth - this.listElement.clientWidth;
    return Math.max(1, maxValue);
  }

  computeIntegerDelta() {
    const rawDelta = this.direction * SCROLL_SPEED + this.offsetAccumulator;
    const integerDelta =
      rawDelta > 0 ? Math.floor(rawDelta) : Math.ceil(rawDelta);
    const remainder = rawDelta - integerDelta;
    return { delta: integerDelta, remainder };
  }

  restartAutoscrollAfterVisibility() {
    if (this.userInterrupted || !this.listElement) return;
    if (this.visibilityRestartId) {
      window.cancelAnimationFrame(this.visibilityRestartId);
    }

    this.cancelAnimation();
    this.rafId = null;
    this.visibilityRestartId = window.requestAnimationFrame(() => {
      this.visibilityRestartId = null;
      this.startAutoscroll();
    });
  }

  setAutoscrollClass(active) {
    if (!this.element) return;

    if (active) {
      this.element.classList.remove(AUTOSCROLL_CLASS, AUTOSCROLL_INITIALIZED_CLASS);
      this.element.classList.add(AUTOSCROLL_INITIALIZED_CLASS, AUTOSCROLL_CLASS);
      return;
    }

    this.element.classList.remove(AUTOSCROLL_CLASS);
  }

  dispatchEvent(name, detail = {}, target = this.element) {
    if (!target) return;
    const event = new CustomEvent(name, {
      detail,
      bubbles: true
    });
    target.dispatchEvent(event);
  }
}
